import { ElementLogic } from '../../../core/canvas-logic/base/element-logic';
import { InteractionEvent } from '@tencent/alloy-renderer';
import SnaplineManager from '@editor/src/page/index/components/canvas/snapline-manager';
import { checkIsSelectable } from '@editor/src/page/index/components/canvas/element-wrapper/utils';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { transaction, reaction, IReactionDisposer } from 'mobx';
import { RenderPostionType } from '@src/js/constant';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { isLogin } from 'utils';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import type { ComponentFactory } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory';
import { ShapeLinkFlagEvents } from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/component/shape-link-flag-component/events';
import { InterfaceShapeLinkFlagEvents } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/shape-link-flag-component/types';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { MenuManagerEventsKeys } from '@src/slide/application/service/menu-manager/menu-manager-events.interface';
import {
    PcMobileCommonLogic,
} from '@src/slide/content-viewer/service/canvas-renderer/entry/base/logic/pc-mobile-common-logic';
import {
    IUserPriviledgeService,
} from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import {
    IComponentFactory,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory.interface';

export class CommonEditLogic extends ElementLogic {
    // 链接图标事件实例
    public flagEventsInstance: InterfaceShapeLinkFlagEvents;

    // 全局状态监听销毁句柄
    public reactionDisposers: IReactionDisposer[] = [];

    public hasEditorRegisterDocumentMouseUp: boolean;

    protected declare readonly component: IComponentFactory;
    // 事件相关标志位
    private mouseDown: boolean;
    // 等到mouseUp变更元素选中状态（比如选中态的组合，如果点击则是取消，如果拖拽则不取消）可以理解为changeSelectedUntilMouseUp
    private needMouseUp: boolean;
    private mouseMove: boolean;
    private pcMobileCommonLogic: PcMobileCommonLogic;

    public constructor(factoryOptions: InterfaceFactoryOptions<IElementBaseModel>, component: ComponentFactory) {
        super(factoryOptions, component);
        this.pcMobileCommonLogic = new PcMobileCommonLogic(factoryOptions);

        // (1)监听插入状态，禁止图形事件（2）监听格式刷事件，变化光标
        this.addStoreStateListener();

        // 链接标识小图标对应事件
        this.addShapeLinkFlagListener();

        // 选中、拖拽
        this.component.wrapper.onMouseDown(this.handleMouseDown);
        this.component.wrapper.onMouseUp(this.handleMouseUp);
        this.component.wrapper.onMouseMove(this.handleMouseMove);
        // 不可编辑提示（其他操作细化在各图形类中）
        this.component.wrapper.onDoubleClick(this.handleDoubleClick);
        // 图形|图片在设置编辑链接后，只读态点击跳转
        this.component.wrapper.onClick(this.handleClick);
        // 图形|图片在选中且编辑链接后，hover有悬浮提示
        this.component.wrapper.onMouseEnter(this.handleMouseEnter);
        this.component.wrapper.onMouseLeave(this.handleMouseLeave);
        // 拖拽图片填充图形背景
        this.component.wrapper.onDrop(this.handleDrop);
    }

    public dispose(): void {
        super.dispose();
        this.flagEventsInstance?.cancelAllEvents?.();
    }

    protected handleMouseDown = (e: InteractionEvent): void => {
        if (!this.checkIsDoMouseAction()) {
            return;
        }
        this.mouseDown = true;
        if (browser && !browser.isMac) {
            SnaplineManager.getInstance().clear(); // bugfix for http://tapd.oa.com/10143861/bugtrace/bugs/view?bug_id=1010143861073980447
        }
        (this.getAppService() as IPcEditorAppServices).editableServiceLayer?.selectedAnims?.clear();
        transaction(() => {
            if (this.factoryOptions.isGroupChildren) {
                this.pcMobileCommonLogic.findRootGroupMouseDown(e, this.factoryOptions.model);
            } else {
                e.stopPropagation();
                // e.preventDefault();
                this.getSelectionService().selectedElements.clear(SelectedEleMgrType.GROUP);

                if (this.isSpecialKey(e.nativeEvent)) { // shift/control/command按住时多选
                    if (this.factoryOptions.model.selected) {
                        this.needMouseUp = true;
                    } else {
                        this.getSelectionService().selectedElements.add(this.factoryOptions.model);
                        this.getAppService().viewableServiceLayer.editingStateManager.updateElementCropState(false);
                        this.reportOnSelect(this.factoryOptions.model);
                    }
                } else if (!this.factoryOptions.model.selected) {
                    this.selectElement(this.factoryOptions.model);
                } else if (this.factoryOptions.model.type === UIModelType.GROUP
                    && this.getSelectionService().selectedElements
                        .selectedArrayForType(SelectedEleMgrType.SLIDE).length > 1) {
                    this.needMouseUp = true;
                }
                if (this.factoryOptions.model.type !== UIModelType.TABLE
                    && this.getAppService().viewableServiceLayer.userPriviledgeService.isEditable()) {
                    (
                        this.getAppService() as IEditorAppServices
                    ).editableServiceLayer?.dragElementHandler.handleMouseDownOnElement(
                        e,
                        this.factoryOptions.model,
                        this.factoryOptions.getPageScale(),
                        this.needMouseUp,
                        () => {
                            this.needMouseUp = false;
                        },
                    );
                }
            }
        });
    };

    protected getAppService(): IEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as IEditorAppServices;
    }

    protected getUserPriviledgeService(): IUserPriviledgeService {
        return this.getAppService().viewableServiceLayer.userPriviledgeService;
    }

    protected getImagePreviewService() {
        return (this.getAppService() as IPcEditorAppServices).viewableServiceLayer?.imagePreviewService;
    }

    protected getSelectionService(): ISelectionService {
        return (AppServiceProvider.getInstance().getAppServices() as
            IEditorAppServices).viewableServiceLayer.selectionService;
    }

    protected getMenuManager(): IMenuManager | undefined {
        return (AppServiceProvider.getInstance().getAppServices() as
            IEditorAppServices).editableServiceLayer?.menuManager;
    }

    protected getAppServices(): IEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as IEditorAppServices;
    }

    /**
     * 选中某个元素
     * @param childModel
     */
    protected selectElement(childModel) {
        this.getSelectionService().selectedElements.clear();
        this.getSelectionService().selectedElements.add(childModel);
        this.getAppService().viewableServiceLayer.editingStateManager.updateElementCropState(false);
        this.reportOnSelect(childModel);
    }

    private handleMouseUp = (e: InteractionEvent) => {
        if (!this.checkIsDoMouseAction()) {
            return;
        }

        (this.getAppService() as IPcEditorAppServices).editableServiceLayer?.selectedAnims?.clear();
        // @ts-ignore
        if (e.nativeEvent.which === 1) {
            this.handleActionEnd(e);
        }
        if (!this.hasEditorRegisterDocumentMouseUp) { // 解决鼠标划出Element收不到up事件问题
            const { formatBrushManager, animBrushManager } = (this.getAppService() as IPcEditorAppServices)
                .editableServiceLayer || {};
            if (animBrushManager?.animBrushState) {
                animBrushManager?.onElementSelectionMouseUp();
            } else if (formatBrushManager?.formatBrushState) {
                formatBrushManager?.onElementSelectionMouseUp();
            }
        }
        if (!this.mouseMove) {
            dispatchEvent(new Event('changeIframeEvent'));
        }
        this.mouseMove = false;
        this.mouseDown = false;
    };

    private handleActionEnd = (e: InteractionEvent) => {
        if (this.factoryOptions.isGroupChildren) {
            this.pcMobileCommonLogic.findRootGroupMouseUp(e, this.factoryOptions.model);
        } else if (this.needMouseUp) {
            if (!this.factoryOptions.model.selected) {
                this.getSelectionService().selectedElements.add(this.factoryOptions.model);
            } else {
                this.getSelectionService().selectedElements.remove(this.factoryOptions.model);
            }
            this.needMouseUp = false;
        }
    };

    private isSpecialKey = e => e.metaKey || e.shiftKey || e.ctrlKey;

    private isSubGroup = () => this.factoryOptions.model.type === UIModelType.GROUP
        && this.factoryOptions.isGroupChildren;

    private checkIsDoMouseAction = () => checkIsSelectable(this.factoryOptions.model)
        && (!browser.isMobile || browser.isIpadAndTencentDocApp)
        && !this.isSubGroup();

    private handleMouseMove = () => {
        const state = checkIsSelectable(this.factoryOptions.model)
            && (!browser.isMobile || browser.isIpadAndTencentDocApp);
        if (!state) {
            return;
        }
        if (this.mouseDown) {
            this.mouseMove = true;
        }
    };

    // 只读态 点击设置链接的图形或图片 直接跳转
    private linkInReadOnlyJump = () => {
        const { model } = this.factoryOptions;
        const { type, currentEditState, props, key } = model || {};
        if (
            [UIModelType.SHAPE, UIModelType.IMAGE].includes(type)
            && !props?.addon // 在非编辑态为插件时点击不直接跳转链接
            && currentEditState !== PPTEditorStateType.EDITABLE
        ) {
            const data = props.link?.href;
            const href = data ? this.getAppService().viewableServiceLayer
                .linkManager.getLinkUrlWithData(data) : undefined;
            if (href) {
                this.getMenuManager()?.event.emit(MenuManagerEventsKeys.LINK_CLICK, key);
            }
        }
    };

    private handleClick = () => {
        this.linkInReadOnlyJump();
    };

    private handleDoubleClick = (e) => {
        // wrapper上阻止一下双击的冒泡，不应该让透到slide那一层
        e?.stopPropagation();

        if (!isLogin() && this.getUserPriviledgeService().pptEditorState > PPTEditorStateType.EDITABLE) {
            this.getAppService().viewableServiceLayer.tipsManager.showCannotEditTips();
        }
    };

    /**
     * 图形|图片在选中且编辑链接后，hover有悬浮提示
     */
    private handleMouseEnter = () => {
        const { region } = this.factoryOptions;
        const { type, selected, key } = this.factoryOptions.model;
        if ([UIModelType.SHAPE, UIModelType.IMAGE].includes(type) && selected && region === RenderPostionType.CANVAS) {
            this.getMenuManager()?.event.emit(MenuManagerEventsKeys.ENTER_ELEMENT, key);
        }
    };

    private handleMouseLeave = () => {
        const { region } = this.factoryOptions;
        const { type, selected, key } = this.factoryOptions.model;
        if ([UIModelType.SHAPE, UIModelType.IMAGE].includes(type) && selected && region === RenderPostionType.CANVAS) {
            this.getMenuManager()?.event.emit(MenuManagerEventsKeys.LEAVE_ELEMENT, key);
        }
    };

    // 拖拽图片到图形上，填充背景
    private handleDrop = async () => {
        const { model } = this.factoryOptions;
        // 保存model，交给copy-paste处理
        if ((model as IElementBaseModel).selectable) { // 可选择才去替换
            this.getAppService().editableServiceLayer?.copyPasteManager?.promise().then((copyPaste) => {
                copyPaste.savedDropModel = model;
            });
        }
    };

    // 监听插入状态、格式刷状态
    private addStoreStateListener = () => {
        this.reactionDisposers.push(reaction(() => {
            const { insertState } = this.getAppService().viewableServiceLayer.editingStateManager;
            const formatBrushState = (this.getAppService() as IPcEditorAppServices)
                .editableServiceLayer?.formatBrushManager?.formatBrushState;
            return [insertState, formatBrushState];
        }, () => {
            this.component.wrapper.updateElementShowAndEventStatus();
        }));
    };

    // 链接小图标事件监听
    private addShapeLinkFlagListener = () => {
        if (!this.component.inner?.shapeLinkFlagComponent) {
            return;
        }
        this.flagEventsInstance = new ShapeLinkFlagEvents(this.component.inner.shapeLinkFlagComponent);
        // 已经有链接图标需要绑定相关事件
        if (this.component.inner.shapeLinkFlagComponent.flagGraphics) {
            this.flagEventsInstance.registerFlagAboutEvents();
        }
    };

    /**
     * 选中元素后上报
     * @param model
     */
    private reportOnSelect = (model: IElementBaseModel) => {
        if (this.factoryOptions.model.currentEditState !== PPTEditorStateType.EDITABLE) return;
        let action = '';
        if (model.type === UIModelType.SHAPE) {
            action = TDWActionType.CLK_EDIT;
        }
        if (model.type === UIModelType.IMAGE) {
            action = TDWActionType.CLK_IMG;
        }
        if (model.type === UIModelType.GROUP) {
            action = TDWActionType.CLK_COMBIN;
        }
        if (action) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC_OBJ,
                action,
            });
        }
    };
}
